import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  facebookLoginAPI,
  googleLoginAPI,
  loginAPI,
  requestActivationAPI,
} from "../../../redux/features/authSlice";
import toast from "react-hot-toast";
import CustomButton from "../../../components/CustomButton/CustomButton";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";

export default function Login() {
  const isGoogleLoginConfigured = Boolean(
    process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim()
  );
  const [passwordShow, setPasswordShow] = useState(false);
  const [userVerified, setUserVerified] = useState(false);
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [userArgument, setUserArgument] = useState("");
  const [modalEmail, setModalEmail] = useState("");
  const [modalEmailError, setModalEmailError] = useState("");
  const [userArgumentError, setUserArgumentError] = useState("");
  const [fbSdkLoaded, setFbSdkLoaded] = useState(false); // Track SDK loading
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.auth);
  // Initialize Facebook SDK
  useEffect(() => {
    // Check if SDK is already loaded
    if (window.FB) {
      setFbSdkLoaded(true);
      return;
    }

    // Load and initialize SDK
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: "1804474090278666",
        cookie: true,
        xfbml: true,
        version: "v18.0",
      });
      console.log("Facebook SDK initialized");
      setFbSdkLoaded(true);
    };

    // Load SDK script if not already loaded
    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.onload = () => console.log("Facebook SDK script loaded");
      script.onerror = () => {
        console.error("Failed to load Facebook SDK");
        toast.error("Failed to load Facebook SDK. Please try again.", {
          position: "top-center",
        });
      };
      document.body.appendChild(script);
    }

    // Cleanup
    return () => {
      delete window.fbAsyncInit;
    };
  }, []);

  const initialValues = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[@$!%*?&]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const result = await dispatch(loginAPI(values)).unwrap();
      console.log("google login responce", result);
      if (result.success) {
        if (result.data.user.is_verified) {
          toast.success(result.message, {
            position: "top-center",
          });

          if (
            result.data.role === "Instructor" &&
            result.data.status === "pending"
          ) {
            navigate("/profile-setup");
          } else if (
            result.data.role === "Instructor" &&
            result.data.status === "complete"
          ) {
            navigate("/instructor-dashboard");
          } else if (
            result.data.role === "Student" &&
            result.data.paymentCheckStatus === true
          ) {
            navigate("/student-dashboard");
          } else if (
            result.data.role === "Student" &&
            result.data.paymentCheckStatus === false
          ) {
            navigate("/choose-plan");
          } else if (result.data.role === "Admin") {
            navigate("/admin-dashboard");
          }
        } else {
          toast.error("User is not verified", {
            position: "top-center",
          });
          setUserVerified(true);
        }
      }
    } catch (error) {
      const errorMessage =
        error?.message || error || "Login failed. Please try again.";
      if (errorMessage.includes("Your account has been deactivated")) {
        // Extract reason from error message
        const reasonMatch = errorMessage.match(
          /Reason: (.*?)\. Please contact/
        );
        const reason = reasonMatch ? reasonMatch[1] : "No reason provided";
        setDeactivationReason(reason);
        setShowDeactivatedModal(true);
      } else {
        toast.error(errorMessage, {
          position: "top-center",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validateEmail = (email) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleActivationRequest = async () => {
    let valid = true;
    setModalEmailError("");
    setUserArgumentError("");

    if (!modalEmail.trim()) {
      setModalEmailError("Email is required.");
      valid = false;
    } else if (!validateEmail(modalEmail.trim())) {
      setModalEmailError("Please enter a valid email address.");
      valid = false;
    }

    if (!userArgument.trim()) {
      setUserArgumentError(
        "Please provide a reason for your reactivation request."
      );
      valid = false;
    }

    if (!valid) return;

    try {
      const resultAction = await dispatch(
        requestActivationAPI({
          email: modalEmail.trim(),
          argument: userArgument,
        })
      );
      if (requestActivationAPI.fulfilled.match(resultAction)) {
        toast.success(
          "Activation request sent successfully. You will be notified once your account is activated.",
          { position: "top-center" }
        );
        setShowDeactivatedModal(false);
        setUserArgument("");
        setModalEmail("");
      } else {
        toast.error(
          resultAction.payload ||
          "Failed to send activation request. Please try again.",
          {
            position: "top-center",
          }
        );
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.", {
        position: "top-center",
      });
    }
  };

  // google login
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      let token = tokenResponse.access_token;
      try {
        const res = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const userInfo = res.data;
        if (token) {
          dispatch(
            googleLoginAPI({
              token,
              userInfo,
            })
          )
            .then((action) => {
              if (action.error) {
                toast.error(action.payload, { position: "top-center" });
              } else {
                toast.success("Google login successful!", {
                  position: "top-center",
                });
                // Access paymentCheckStatus from action.payload.data
                const paymentCheckStatus =
                  action.payload.data.paymentCheckStatus;
                const role = action.payload.data.role; // Assuming role is also in data
                console.log("role google", role);
                console.log(
                  "paymetn check status google",
                  paymentCheckStatus
                );
                if (role === "Student") {
                  if (paymentCheckStatus) {
                    // Handle case when paymentCheckStatus is true
                    navigate("/dashboard"); // Example: Navigate to dashboard if payment is done
                  } else {
                    // Handle case when paymentCheckStatus is false
                    navigate("/choose-plan");
                  }
                }
              }
            })
            .catch((error) => {
              console.log(error);
            });
        }
      } catch (error) {
        toast.error("Failed to fetch user info", { position: "top-center" });
      }
    },
    onError: (error) => {
      console.error("Login Failed", error);
    },
  });

  const handleFacebookLogin = () => {
    // Synchronous wrapper for async logic
    if (!fbSdkLoaded || !window.FB) {
      toast.error("Facebook login is not ready. Please try again.", {
        position: "top-center",
      });
      return;
    }

    // Check if running over HTTPS
    if (window.location.protocol !== "https:") {
      toast.error("Facebook login requires a secure connection (HTTPS).", {
        position: "top-center",
      });
      return;
    }

    async function performFacebookLogin() {
      try {
        const response = await new Promise((resolve) => {
          window.FB.login((res) => resolve(res), {
            scope: "public_profile,email",
          });
        });

        if (response.authResponse && response.authResponse.accessToken) {
          const token = response.authResponse.accessToken;
          const res = await axios.get(
            `https://graph.facebook.com/me?fields=id,name,email,picture&access_token=${token}`
          );
          const userInfo = res.data;

          await dispatch(
            facebookLoginAPI({
              token,
              userInfo: {
                id: userInfo.id,
                name: userInfo.name,
                email: userInfo.email || "",
                picture: userInfo.picture?.data?.url || "",
              },
            })
          ).then((action) => {
            if (action.error) {
              throw new Error(action.payload || "Facebook login failed");
            }
            toast.success("Facebook login successful!", {
              position: "top-center",
            });
            // Access paymentCheckStatus and role from action.payload.data
            const paymentCheckStatus = action.payload.data.paymentCheckStatus;
            const role = action.payload.data.role;
            console.log("role facebook", role);
            console.log("paymetn check status facebook", paymentCheckStatus);

            if (role === "Student") {
              if (paymentCheckStatus) {
                // Navigate to dashboard if payment is done
                navigate("/dashboard");
              } else {
                // Navigate to choose-plan if payment is not done
                navigate("/choose-plan");
              }
            }
          });
        } else {
          toast.error("Facebook login cancelled or failed. Please try again.", {
            position: "top-center",
          });
        }
      } catch (error) {
        toast.error(
          error.message ||
          "An error occurred during Facebook login. Please try again.",
          {
            position: "top-center",
          }
        );
      }
    }

    performFacebookLogin();
  };

  return (
    <div className="d-flex flex-column flex-grow-1 justify-content-center px-15">
      <div className="text-center mb-20">
        <h1 className="ah-heading-02 fw-semibold mb-3">Welcome!</h1>
        <p className="black-100 ah-text-base">
          Log in with your University Credentials
        </p>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form
            action="#"
            className="bg-white auth-form-holder mx-auto px-20 px-sm-30 px-md-40 pt-40 pt-md-50 pb-30"
          >
            <div className="mb-12 mb-md-16">
              <label
                htmlFor="email"
                className="form-label black-100 fw-semibold mb-8 mb-lg-12"
              >
                Email
              </label>
              <Field
                type="email"
                name="email"
                id="email"
                placeholder="Enter your email"
                className="form-control"
              />
              <ErrorMessage
                name="email"
                component="div"
                className="text-danger mt-1"
              />
            </div>
            <div className="mb-12 mb-md-16">
              <label
                htmlFor="password"
                className="form-label black-100 fw-semibold mb-8 mb-lg-12"
              >
                Password
              </label>
              <div className="position-relative">
                <Field
                  type={passwordShow ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="Enter your password"
                  className="form-control"
                />
                {!passwordShow ? (
                  <svg
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "10px",
                      transform: "translateY(-50%)",
                      width: "20px",
                    }}
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fas"
                    data-icon="eye-slash"
                    className="cursor-pointer"
                    onClick={() => setPasswordShow(!passwordShow)}
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 512"
                  >
                    <path
                      fill="currentColor"
                      d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "10px",
                      transform: "translateY(-50%)",
                      width: "20px",
                    }}
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fas"
                    data-icon="eye"
                    className="cursor-pointer"
                    onClick={() => setPasswordShow(!passwordShow)}
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 576 512"
                  >
                    <path
                      fill="currentColor"
                      d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 97-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"
                    ></path>
                  </svg>
                )}
              </div>
              <ErrorMessage
                name="password"
                component="div"
                className="text-danger mt-1"
              />
              <div className="text-end pt-4 pt-md-10">
                <Link
                  to={"/forgot-password"}
                  className="ah-text-sm black-100-78 fw-semibold"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>
            <div className="pt-12 mb-18 mb-md-24">
              <CustomButton
                type={"submit"}
                label="Login"
                classes={"btn btn-primary w-100 mb-10"}
                isSubmitting={isSubmitting}
                disabled={isSubmitting || !isValid || !dirty}
              />
            </div>
            {userVerified && (
              <div className="text-center">
                <Link
                  to={`/forgot-password?userVerified=${true}`}
                  className="theme-color fw-semibold text-center mb-12 d-inline-block text-decoration-underline"
                >
                  Please verify your account.
                </Link>
              </div>
            )}
            <div className="text-center text-base">
              Don’t have an account?{" "}
              <Link to={"/user-type"} className="fw-semibold text-green">
                Sign up
              </Link>
            </div>
          </Form>
        )}
      </Formik>
      <div className="mx-auto" style={{ maxWidth: "650px", width: "100%" }}>
        <div className="d-flex gap-2 align-items-center my-24">
          <div className="border-bottom flex-grow-1 border-secondary border-opacity-50"></div>
          <div className="text-black fw-bold ah-text-md">or</div>
          <div className="border-bottom flex-grow-1 border-secondary border-opacity-50"></div>
        </div>
        <div className="">
          <button
            type="button"
            className="btn btn-white no-shadow w-100 mb-14"
            onClick={() => {
              if (!isGoogleLoginConfigured) {
                toast.error("Google login is not configured. Add REACT_APP_GOOGLE_CLIENT_ID in the frontend .env file.", {
                  position: "top-center",
                });
                return;
              }
              googleLogin();
            }}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="me-5"
            >
              <g clipPath="url(#clip0_2149_3438)">
                <path
                  d="M18.8 10.3124C18.8 9.66238 18.7417 9.03738 18.6333 8.43738H10V11.9832H14.9333C14.7208 13.129 14.075 14.0999 13.1042 14.7499V17.0499H16.0667C17.8 15.454 18.8 13.104 18.8 10.3124Z"
                  fill="#4285F4"
                />
                <path
                  d="M10 19.2707C12.475 19.2707 14.55 18.4499 16.0667 17.0499L13.1042 14.7499C12.2833 15.2999 11.2333 15.6249 10 15.6249C7.61252 15.6249 5.59168 14.0124 4.87085 11.8457H1.80835V14.2207C3.31668 17.2165 6.41668 19.2707 10 19.2707Z"
                  fill="#34A853"
                />
                <path
                  d="M4.87075 11.8456C4.68742 11.2956 4.58325 10.7081 4.58325 10.104C4.58325 9.4998 4.68742 8.9123 4.87075 8.3623V5.9873H1.80825C1.16659 7.26469 0.832686 8.67448 0.833253 10.104C0.833253 11.5831 1.18742 12.9831 1.80825 14.2206L4.87075 11.8456Z"
                  fill="#FBBC05"
                />
                <path
                  d="M10 4.58321C11.3458 4.58321 12.5542 5.04571 13.5042 5.95404L16.1333 3.32488C14.5458 1.84571 12.4708 0.937378 10 0.937378C6.41668 0.937378 3.31668 2.99154 1.80835 5.98738L4.87085 8.36238C5.59168 6.19571 7.61252 4.58321 10 4.58321Z"
                  fill="#EA4335"
                />
              </g>
              <defs>
                <clipPath id="clip0_2149_3438">
                  <rect
                    width="20"
                    height="20"
                    fill="white"
                    transform="translate(0 0.104004)"
                  />
                </clipPath>
              </defs>
            </svg>
            Continue with Google
          </button>
          <button
            type="button"
            className="btn btn-white no-shadow w-100 mb-14"
            onClick={() => handleFacebookLogin()}
          >
            <svg
              width="21"
              height="21"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="me-5"
            >
              <g clip-path="url(#clip0_2149_3442)">
                <path
                  d="M20.5 10.104C20.5 4.58117 16.0229 0.104027 10.5 0.104027C4.97715 0.104027 0.5 4.58117 0.5 10.104C0.5 15.0953 4.15686 19.2323 8.9375 19.9825V12.9947H6.39844V10.104H8.9375V7.9009C8.9375 5.39465 10.4304 4.01028 12.7146 4.01028C13.8087 4.01028 14.9531 4.20559 14.9531 4.20559V6.66653H13.6921C12.4499 6.66653 12.0625 7.43737 12.0625 8.22819V10.104H14.8359L14.3926 12.9947H12.0625V19.9825C16.8431 19.2323 20.5 15.0953 20.5 10.104Z"
                  fill="#1877F2"
                />
                <path
                  d="M14.3926 12.9946L14.8359 10.104H12.0625V8.22816C12.0625 7.43734 12.4499 6.6665 13.6921 6.6665H14.9531V4.20557C14.9531 4.20557 13.8087 4.01025 12.7146 4.01025C10.4304 4.01025 8.9375 5.39463 8.9375 7.90088V10.104H6.39844V12.9946H8.9375V19.9825C9.44662 20.0624 9.96844 20.104 10.5 20.104C11.0316 20.104 11.5534 20.0624 12.0625 19.9825V12.9946H14.3926Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_2149_3442">
                  <rect
                    width="20"
                    height="20"
                    fill="white"
                    transform="translate(0.5 0.104004)"
                  />
                </clipPath>
              </defs>
            </svg>
            Continue with Facebook
          </button>
          <button type="button" className="btn btn-white no-shadow w-100 mb-14">
            <svg
              width="19"
              height="20"
              viewBox="0 0 19 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="me-5"
            >
              <g clip-path="url(#clip0_2149_3463)">
                <path
                  d="M14.4141 0.854004H4.58594C2.19127 0.854004 0.25 2.79527 0.25 5.18994V15.0181C0.25 17.4127 2.19127 19.354 4.58594 19.354H14.4141C16.8087 19.354 18.75 17.4127 18.75 15.0181V5.18994C18.75 2.79527 16.8087 0.854004 14.4141 0.854004Z"
                  fill="white"
                />
                <path
                  d="M14.4141 0.854004H4.58594C2.19127 0.854004 0.25 2.79527 0.25 5.18994V15.0181C0.25 17.4127 2.19127 19.354 4.58594 19.354H14.4141C16.8087 19.354 18.75 17.4127 18.75 15.0181V5.18994C18.75 2.79527 16.8087 0.854004 14.4141 0.854004Z"
                  fill="#0A66C2"
                />
                <path
                  d="M13.5985 16.5851H15.7138C15.7904 16.5851 15.8639 16.5547 15.9181 16.5005C15.9723 16.4463 16.0028 16.3728 16.0028 16.2962L16.0039 11.8271C16.0039 9.49126 15.5006 7.69582 12.7709 7.69582C11.7332 7.65723 10.7547 8.19214 10.2266 9.08535C10.224 9.08969 10.2201 9.09306 10.2154 9.09496C10.2108 9.09685 10.2056 9.09717 10.2007 9.09585C10.1958 9.09453 10.1915 9.09165 10.1885 9.08765C10.1854 9.08366 10.1837 9.07876 10.1836 9.07371V8.20053C10.1836 8.12386 10.1532 8.05034 10.099 7.99613C10.0448 7.94192 9.97123 7.91147 9.89457 7.91147H7.88725C7.81058 7.91147 7.73706 7.94192 7.68285 7.99613C7.62864 8.05034 7.59819 8.12386 7.59819 8.20053V16.2957C7.59819 16.3724 7.62864 16.4459 7.68285 16.5001C7.73706 16.5543 7.81058 16.5848 7.88725 16.5848H10.0023C10.079 16.5848 10.1525 16.5543 10.2067 16.5001C10.2609 16.4459 10.2914 16.3724 10.2914 16.2957V12.2942C10.2914 11.1627 10.506 10.0669 11.9088 10.0669C13.2916 10.0669 13.3095 11.3616 13.3095 12.3674V16.2961C13.3095 16.3727 13.3399 16.4463 13.3941 16.5005C13.4484 16.5547 13.5219 16.5851 13.5985 16.5851ZM2.99609 5.16306C2.99609 6.02042 3.70191 6.72587 4.55934 6.72587C5.41656 6.7258 6.12194 6.01991 6.12194 5.1627C6.1218 4.30548 5.41634 3.6001 4.55905 3.6001C3.70155 3.6001 2.99609 4.3057 2.99609 5.16306ZM3.49899 16.5851H5.61688C5.69354 16.5851 5.76707 16.5547 5.82128 16.5005C5.87549 16.4463 5.90594 16.3727 5.90594 16.2961V8.20053C5.90594 8.12386 5.87549 8.05034 5.82128 7.99613C5.76707 7.94192 5.69354 7.91147 5.61688 7.91147H3.49899C3.42233 7.91147 3.3488 7.94192 3.29459 7.99613C3.24038 8.05034 3.20993 8.12386 3.20993 8.20053V16.2961C3.20993 16.3727 3.24038 16.4463 3.29459 16.5005C3.3488 16.5547 3.42233 16.5851 3.49899 16.5851Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_2149_3463">
                  <rect
                    width="18.5"
                    height="18.5"
                    fill="white"
                    transform="translate(0.25 0.854004)"
                  />
                </clipPath>
              </defs>
            </svg>
            Continue with Linkedin
          </button>
        </div>
      </div>
      {/* Deactivated Account Modal */}
      <Modal
        show={showDeactivatedModal}
        onHide={() => {
          setShowDeactivatedModal(false);
          setUserArgument("");
          setModalEmail("");
          setModalEmailError("");
          setUserArgumentError("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Account Deactivated</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Your account has been deactivated.
            <br />
            <strong>Reason:</strong> {deactivationReason}
            <br />
            Please provide your reason for requesting account reactivation:
          </p>
          <div className="mb-3">
            <label
              htmlFor="modalEmail"
              className="form-label black-100 fw-semibold mb-2"
            >
              Email
            </label>
            <input
              type="email"
              id="modalEmail"
              className="form-control"
              value={modalEmail}
              onChange={(e) => setModalEmail(e.target.value)}
              placeholder="Enter your email"
            />
            {modalEmailError && (
              <div className="text-danger mt-1">{modalEmailError}</div>
            )}
          </div>
          <div className="mb-3">
            <label
              htmlFor="reactivationReason"
              className="form-label black-100 fw-semibold mb-2"
            >
              Reason for Reactivation
            </label>
            <textarea
              className="form-control"
              id="reactivationReason"
              rows="4"
              value={userArgument}
              onChange={(e) => setUserArgument(e.target.value)}
              placeholder="Enter your reason for reactivation"
            />
            {userArgumentError && (
              <div className="text-danger mt-1">{userArgumentError}</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeactivatedModal(false);
              setUserArgument("");
              setModalEmail("");
              setModalEmailError("");
              setUserArgumentError("");
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleActivationRequest}>
            Send Request
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
